/** /notifications/ — cross-tenant notification management (grid + editor).
 *
 * Hand-authored wrapper (console convention: feature modules never depend on
 * generated types). The `filters` param is the QuerySearch wire AST
 * (NotificationFilterNode in the OpenAPI spec).
 *
 * The detail payload's `notification` / `delivery_methods` / `block_refs`
 * sections use the exact field names the PATCH body accepts AND the exact
 * shape Notification_Change snapshots store — restore = load a snapshot into
 * the editor and save normally.
 */
import type { SuggestionFetcher, WireNode } from '@/components/QuerySearch';

import type { ManagedAspects, MaterialiseResult } from './cannedNotifications';
import { apiGet, apiPatch, apiPost, extendedListConfig } from './client';
import type { RulesetBrief, RuleWire, RuleWriteRow } from './notificationRulesets';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

// ---------------------------------------------------------------------------
// Enums / labels (mirror demo/lib/events/constants.py + model choices)
// ---------------------------------------------------------------------------

export const DELIVERY_METHODS = {
  EMAIL: 0,
  SMS: 1,
  CHAT: 2,
  PUSH_NOTIFICATION: 3,
  SENTRY: 4,
  SNS_TOPIC: 5,
  HTTP_REQUEST: 6,
  WHATSAPP: 7,
  AIRBNB: 8,
} as const;
export type DeliveryMethodCode = (typeof DELIVERY_METHODS)[keyof typeof DELIVERY_METHODS];

export const DELIVERY_METHOD_LABELS: Record<number, string> = {
  0: 'Email',
  1: 'SMS',
  2: 'Chat',
  3: 'Push',
  4: 'Sentry',
  5: 'SNS',
  6: 'HTTP',
  7: 'WhatsApp',
  8: 'Airbnb',
};

/** Channels with a STANDARD/CUSTOM rendering toggle + their NDM field names. */
export const RENDER_STANDARD = 0;
export const RENDER_CUSTOM = 1;

export const TOUR_OVERRIDE_MODE_LABELS: Record<number, string> = {
  0: 'Send always',
  1: 'Send if no tour-specific override',
  2: 'Tour-specific override',
};

/** Group_Tour_User.BOOKING_STATES — full 5-state map. */
export const BOOKING_STATE_LABELS: Record<number, string> = {
  0: 'On hold',
  1: 'Booked',
  2: 'Cancelled',
  3: 'Cancelled on hold',
  4: 'Pending confirmation',
};

export const METHOD_ORDERING_LABELS: Record<number, string> = {
  0: 'Use all delivery methods',
  1: 'Stop at first successful',
  2: 'Advanced',
};
export const METHOD_ORDERING_ADVANCED = 2;

export const LOCK_LABELS: Record<number, string> = {
  0: 'None',
  1: 'User-based',
};

/** Notification_Delivery_Method_Advanced_Rule.CONDITIONS. */
export const ADVANCED_CONDITION_LABELS: Record<number, string> = {
  0: 'Successful',
  1: 'Unsuccessful',
  2: 'Read',
  3: 'Unread',
};
/** TRIGGER_CONDITION_ANY_ALL: 0 = all triggers, 1 = any trigger. */
export const ADVANCED_CONDITION_ALL = 0;
export const ADVANCED_CONDITION_ANY = 1;

export const CHANGE_SOURCE_LABELS: Record<string, string> = {
  console: 'Ops Console',
  dashboard: 'Dashboard',
  admin: 'Django admin',
  api: 'API',
  management: 'Management command',
  celery: 'Celery task',
  signal: 'Signal cascade',
  integration: 'Integration',
  system: 'System',
  mcp: 'MCP',
  chat: 'Console agent',
  translation_sync: 'Translation sync',
  revert: 'Revert',
};

/** NDM secret sentinel — the funnel treats it as "unchanged". */
export const SECRET_SENTINEL = '•••';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TenantBrief {
  id: number;
  code: string;
  name: string;
  is_franchise?: boolean;
  parent_id?: number | null;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

export interface LanguagePolicyBrief {
  id: number;
  codename: string;
}

export interface DeliveryMethodSummary {
  id: number;
  method: number;
  method_label: string;
  enabled: boolean;
  sequence: number;
  rendering_type: number | null;
  theme_codename: string | null;
}

export interface NotificationListItem {
  id: number;
  tenant: TenantBrief | null;
  name: string;
  event: string;
  enabled: boolean;
  tour_override_mode: number;
  booking_state: number;
  language_policy: LanguagePolicyBrief | null;
  disable_default_blocks: boolean;
  delivery_methods: DeliveryMethodSummary[];
  block_ref_count: number | null;
  created_ts: string | null;
  updated_ts: string | null;
}

/** Notification scalars + M2M id lists — funnel/PATCH field names. */
export interface NotificationCore {
  name: string;
  event: string;
  enabled: boolean;
  tour_operator: number | null;
  language_policy: number | null;
  tour_override_mode: number;
  timedelta: number | null;
  delivery_method_ordering: number;
  apply_to_guests: boolean;
  apply_to_observers: boolean;
  ignore_user_nf_prefs: boolean;
  rules_match_type: number;
  booking_state: number;
  lock_delivery: number;
  lock_time: number;
  disable_default_blocks: boolean;
  tours: number[];
  roles: number[];
  tags: number[];
  tag_categories: number[];
  atag: number[];
  attachments: number[];
  [key: string]: unknown;
}

/** Full NDM row — every concrete field keyed by model field name (FKs are ids). */
export interface NotificationDeliveryMethod {
  id: number;
  method: number;
  sequence: number;
  enabled: boolean;
  theme: number | null;
  email_subject: string | null;
  email_message: string | null;
  email_message_text: string | null;
  email_rendering_type: number;
  email_template: number | null;
  sms_message: string | null;
  sms_rendering_type: number;
  sms_template: number | null;
  whatsapp_message: string | null;
  whatsapp_rendering_type: number;
  whatsapp_template: number | null;
  chat_message: string | null;
  chat_rendering_type: number;
  chat_template: number | null;
  airbnb_message: string | null;
  airbnb_rendering_type: number;
  airbnb_template: number | null;
  sns_subject: string | null;
  sns_message: string | null;
  sentry_message: string | null;
  [key: string]: unknown;
}

export interface BlockRefItem {
  id: number;
  order: number;
  block: number | null;
  block_codename: string | null;
  block_list: number | null;
  block_list_codename: string | null;
  alias: string | null;
}

/** Advanced delivery rule: "if [any|all] of the trigger methods are
 *  <condition>, after N minutes send the dependent methods". Rows are
 *  recreated on every save (never send `id`); method lists are NDM pks of
 *  THIS notification and must not overlap. */
export interface AdvancedRuleRow {
  id: number;
  condition: number;
  condition_any_all: number;
  send_after_min: number | null;
  trigger_notification_delivery_methods: number[];
  dependent_notification_delivery_methods: number[];
}

export type AdvancedRuleWrite = Omit<AdvancedRuleRow, 'id'>;

/** Reduced tag shape the auto-reply funnel expects ({id} minimum). */
export interface AutoReplyTagRef {
  id: number;
  name?: string;
  category?: string;
}

export interface AutoReplyOption {
  id?: number | null;
  /** Persisted order — always stamped from list position on save. */
  sequence: number;
  answer_trigger: string;
  answer_trigger_is_ai_prompt: boolean;
  tag: AutoReplyTagRef | number | null;
  auto_response: string | null;
}

/** "Send another message": guest replies matching an option's trigger get
 *  `auto_response` back (+ optional tag). `timedelta_…`: seconds; positive =
 *  expires BEFORE tour start, negative = after, null = never. Options
 *  reconcile by id (omitted ids are deleted). */
export interface AutoReplies {
  id?: number | null;
  enabled: boolean;
  timedelta_before_tour_start_to_expire_reply: number | null;
  mark_answers_as_read: boolean;
  options: AutoReplyOption[];
}

export interface ThemeBrief {
  id: number;
  codename: string;
  name: string;
}

export interface StandardTemplateBrief {
  id: number;
  name: string;
  version: number;
  template_language: 'jinja2' | 'django';
  method: number;
  is_system: boolean;
}

export interface NotificationRefs {
  tenant: TenantBrief | null;
  language_policy: LanguagePolicyBrief | null;
  event_label: string | null;
  themes: Record<string, ThemeBrief>;
  standard_templates: Record<string, StandardTemplateBrief>;
  tours: { id: number; name: string }[];
  roles: { id: number; name: string }[];
  tags: { id: number; name: string }[];
  tag_categories: { id: number; name: string }[];
  atag: { id: number; name: string }[];
  method_labels: Record<string, string>;
  dashboard_link: string;
  /** Display names for the rule editor's `nested_ruleset` ids (tpl 48). */
  rulesets: Record<string, RulesetBrief>;
  /** Display shapes for auto-reply option tag ids. */
  auto_reply_tags: Record<string, AutoReplyTagRef>;
  /** The canned archetype this notification was materialised from, or null.
   *  The four `managed_*` booleans live on `notification` — they are columns. */
  canned: CannedRef | null;
}

/** "Materialised from" pointer on a notification's detail payload. */
export interface CannedRef {
  id: number;
  codename: string;
  name: string;
  group_codename: string;
  pack_version: string;
  /** True when the archetype has moved on since this notification was built.
   *  Nothing else surfaces it — the notification looks entirely normal. */
  stale: boolean;
  materialised_ts: string | null;
  refreshed_ts: string | null;
}

export interface NotificationDetail {
  id: number;
  notification: NotificationCore;
  delivery_methods: NotificationDeliveryMethod[];
  block_refs: BlockRefItem[];
  rules: RuleWire[];
  advanced_rules: AdvancedRuleRow[];
  reactions: Record<string, unknown>[];
  auto_replies: AutoReplies | null;
  refs: NotificationRefs;
  created_ts: string | null;
  updated_ts: string | null;
  warnings?: Record<string, unknown>;
}

export interface BlockRefWrite {
  id?: number | null;
  block?: number | null;
  block_list?: number | null;
  alias?: string | null;
}

/** One delivery-method row of the PATCH body. `id: null` = CREATE (the funnel
 *  needs at least `method` + `enabled`; `sequence` comes from list position). */
export type DeliveryMethodWrite = Omit<Partial<NotificationDeliveryMethod>, 'id'> & {
  id: number | null;
};

/** Sparse PATCH body. `delivery_methods` / `block_refs` / `rules`, when
 *  present, must be the COMPLETE ordered lists (reconcile-by-id: missing rows
 *  are deleted, `id: null` rows are created, order = list position);
 *  unchanged fields may be omitted per DM row (rule rows are always full). */
export interface NotificationWrite {
  notification?: Partial<NotificationCore>;
  delivery_methods?: DeliveryMethodWrite[];
  block_refs?: BlockRefWrite[];
  rules?: RuleWriteRow[];
  /** COMPLETE list; the backend deletes + recreates the whole set (id-less rows). */
  delivery_method_advanced_rules?: AdvancedRuleWrite[];
  /** The whole object; options reconcile by id, sequence = list position. */
  auto_replies?: AutoReplies;
  comment?: string;
  expected_updated_ts?: string;
}

/** POST /notifications/ body — minimal create; the editor adds the rest. */
export interface NotificationCreateInput {
  tour_operator: number | 'system' | null;
  name: string;
  event: string;
  tour_override_mode?: number;
  /** Tenant tour ids (override mode 2). */
  tours?: number[];
  enabled?: boolean;
  comment?: string;
}

export interface ListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  ordering?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  filters?: WireNode[];
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface ValidateFieldSpec {
  ref?: string;
  source: string;
  language?: 'django' | 'jinja2';
}

export interface TemplateError {
  message: string;
  line: number | null;
}

export interface TemplateWarning {
  code: string;
  message: string;
  line: number | null;
}

export interface ValidateFieldResult {
  ref: string | null;
  ok: boolean;
  errors: TemplateError[];
  warnings: TemplateWarning[];
}

// ---------------------------------------------------------------------------
// Events / schema / editor metadata
// ---------------------------------------------------------------------------

export interface NotificationEvent {
  code: string;
  family: string;
  entity_kind: 'gtu' | 'order' | 'user' | null;
  label: string | null;
}

export type EventSchema = Record<string, unknown>;

export interface MergedEventSchema {
  type: 'object';
  properties: Record<string, unknown>;
  sources: Record<string, string[]>;
  events: string[];
}

export interface EditorTagInfo {
  name: string;
  library: string;
}

export interface EditorBlockInfo {
  codename: string;
  name: string;
  description: string;
  type: string;
  tour_operator: number | null;
  enabled: boolean;
}

export interface EditorMetadata {
  custom_tags: EditorTagInfo[];
  custom_filters: EditorTagInfo[];
  blocks: EditorBlockInfo[];
  themes: (ThemeBrief & { tour_operator: number | null })[];
  events: NotificationEvent[];
}

export interface StandardTemplateLookupItem extends StandardTemplateBrief {
  tenant_code: string | null;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

function toQuery(p: ListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  return usp.toString();
}

export async function list(p: ListParams = {}): Promise<PaginatedResponse<NotificationListItem>> {
  const qs = toQuery(p);
  return apiGet(`/notifications/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

export async function retrieve(id: number): Promise<NotificationDetail> {
  return apiGet(`/notifications/${id}/`);
}

export async function create(body: NotificationCreateInput): Promise<NotificationDetail> {
  return apiPost('/notifications/', body);
}

export async function update(id: number, body: NotificationWrite): Promise<NotificationDetail> {
  return apiPatch(`/notifications/${id}/`, body);
}

export async function duplicate(
  id: number,
  body: { tour_operator?: number | 'system' | null; name?: string } = {},
): Promise<NotificationDetail> {
  return apiPost(`/notifications/${id}/duplicate/`, body);
}

/** Turn pack management of one or more aspects on or off — "unmanage and edit".
 *
 *  Turning an aspect back ON does NOT reapply the pack immediately; the next
 *  refresh does. Re-managing and clobbering in one unannounced step is the kind
 *  of thing that costs someone an afternoon of work.
 */
export async function setManaged(
  id: number,
  body: Partial<ManagedAspects> & { comment?: string },
): Promise<NotificationDetail> {
  return apiPost(`/notifications/${id}/managed/`, body);
}

/** Reapply this notification's canned definition to its managed aspects. */
export async function refreshCanned(
  id: number,
  body: { dry_run?: boolean } = {},
): Promise<MaterialiseResult> {
  return apiPost(`/notifications/${id}/refresh_canned/`, body);
}

export async function validateTemplates(body: {
  fields: ValidateFieldSpec[];
  tour_operator?: number | null;
  event?: string;
}): Promise<{ results: ValidateFieldResult[] }> {
  return apiPost('/notifications/validate/', body);
}

export async function listEvents(): Promise<{ events: NotificationEvent[] }> {
  return apiGet('/notification_events/');
}

export async function getEventSchema(code: string, signal?: AbortSignal): Promise<EventSchema> {
  return apiGet(`/notification_events/schema/?type=${encodeURIComponent(code)}`, { signal });
}

export async function getMergedEventSchema(codes: string[] | 'all'): Promise<MergedEventSchema> {
  const qs = codes === 'all' ? 'all=1' : `types=${encodeURIComponent(codes.join(','))}`;
  return apiGet(`/notification_events/schema/?${qs}`);
}

export async function getEditorMetadata(
  tourOperatorId?: number | 'system' | null,
): Promise<EditorMetadata> {
  const qs = tourOperatorId != null ? `?tour_operator_id=${tourOperatorId}` : '';
  return apiGet(`/notification_events/editor_metadata/${qs}`);
}

/** A tenant support contact (delivery-method destination lookup). */
export interface SupportContact {
  id: number;
  name: string | null;
  contact: string | null;
  contact_type: number;
  enabled: boolean;
}

/** GET /support_contacts/ — `tour_operator_id` is REQUIRED (the lookup is
 *  tenant-scoped; system templates have no contacts). Max 100 rows. */
export async function fetchSupportContacts(
  params: { tour_operator_id: number; contact_type?: number; search?: string },
  signal?: AbortSignal,
): Promise<{ results: SupportContact[] }> {
  const usp = new URLSearchParams({ tour_operator_id: String(params.tour_operator_id) });
  if (params.contact_type !== undefined) usp.set('contact_type', String(params.contact_type));
  if (params.search) usp.set('search', params.search);
  return apiGet(`/support_contacts/?${usp.toString()}`, signal ? { signal } : undefined);
}

export async function listStandardTemplates(params: {
  method?: number | string;
  tour_operator_id?: number | 'system';
  search?: string;
}): Promise<{ results: StandardTemplateLookupItem[] }> {
  const usp = new URLSearchParams();
  if (params.method !== undefined) usp.set('method', String(params.method));
  if (params.tour_operator_id !== undefined)
    usp.set('tour_operator_id', String(params.tour_operator_id));
  if (params.search) usp.set('search', params.search);
  return apiGet(`/standard_templates/?${usp.toString()}`);
}

// ---------------------------------------------------------------------------
// Suggestion fetchers for the QuerySearch namespaces
// ---------------------------------------------------------------------------

export const fetchEventCodeSuggestions: SuggestionFetcher = async (query, _extra, signal) => {
  const res = await apiGet<{ events: NotificationEvent[] }>('/notification_events/', { signal });
  const q = query.toLowerCase();
  return res.events
    .filter((e) => !q || e.code.toLowerCase().includes(q) || (e.label ?? '').toLowerCase().includes(q))
    .slice(0, 20)
    .map((e) => ({ value: e.code, label: e.label ? `${e.code} — ${e.label}` : e.code }));
};

/** Codename suggestions from a paginated console catalog list endpoint. */
function makeCodenameFetcher(path: string): SuggestionFetcher {
  return async (query, _extra, signal) => {
    const usp = new URLSearchParams({ page_size: '20' });
    if (query) usp.set('search', query);
    const res = await apiGet<PaginatedResponse<{ codename: string; name?: string }>>(
      `${path}?${usp.toString()}`,
      { signal },
    );
    return res.results.map((r) => ({
      value: r.codename,
      label: r.name ? `${r.codename} — ${r.name}` : r.codename,
    }));
  };
}

export const fetchThemeSuggestions = makeCodenameFetcher('/notification_themes/');
export const fetchPolicySuggestions = makeCodenameFetcher('/language_policies/');
export const fetchBlockSuggestions = makeCodenameFetcher('/notification_blocks/');
export const fetchBlockListSuggestions = makeCodenameFetcher('/block_lists/');

// Tenant suggestions reuse the shared Bookings autocomplete factory.
export { makeSuggestionFetcher } from './bookings';
